import React from 'react';
import { cn } from '../lib/utils';

/**
 * ThreePositionSlider - A smooth animated 3-position slider control
 *
 * PORTED VERBATIM from BackOfficePro on 2026-10-16 (AJ: "For the Semprion Admin
 * role I want the OL UI to be similar to BOP"), with only the `cn` import path
 * changed for OL's layout. Keep it in step with
 * `C:/Users/AJ/Projects/BOP2/frontend/src/components/ThreePositionSlider.js`;
 * if one is improved, port the change rather than letting the two diverge.
 *
 * off = grey - hidden · restrict = amber - visible but greyed · on = green
 * 
 * @param {string} value - Current value: 'off', 'restrict', or 'on'
 * @param {function} onChange - Callback when value changes
 * @param {boolean} disabled - Whether the slider is disabled (grayed out)
 * @param {boolean} readOnly - Whether the slider is read-only (shows colors but no interaction)
 * @param {string} className - Additional CSS classes
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {object} labels - Custom labels for positions { off: '', restrict: '', on: '' }
 */
const ThreePositionSlider = ({ 
  value = 'off', 
  onChange, 
  disabled = false,
  readOnly = false,
  className = '',
  size = 'md',
  labels = { off: 'Off', restrict: 'Restrict', on: 'On' },
  showLabels = true,
  'data-testid': dataTestId
}) => {
  // Map value to position index (0, 1, 2)
  const positionMap = { off: 0, restrict: 1, on: 2 };
  const position = positionMap[value] ?? 0;
  
  // Size configurations
  const sizes = {
    xs: {
      track: 'w-[52px] h-5',
      thumb: 'w-[18px] h-4',
      label: 'text-[8px]',
      spacing: 'gap-0.5'
    },
    sm: {
      track: 'w-[72px] h-6',
      thumb: 'w-6 h-5',
      label: 'text-[9px]',
      spacing: 'gap-1'
    },
    md: {
      track: 'w-[90px] h-7',
      thumb: 'w-7 h-6',
      label: 'text-[10px]',
      spacing: 'gap-1.5'
    },
    lg: {
      track: 'w-[120px] h-9',
      thumb: 'w-9 h-8',
      label: 'text-xs',
      spacing: 'gap-2'
    }
  };
  
  const sizeConfig = sizes[size] || sizes.md;
  
  // Calculate thumb position (percentage offset)
  const thumbOffset = position * 50; // 0%, 50%, 100% - but capped inside track
  
  // Color based on position (readOnly still shows colors, disabled grays out)
  const getThumbColor = () => {
    if (disabled && !readOnly) return 'bg-zinc-300';
    switch (position) {
      case 0: return 'bg-zinc-400'; // Off - gray
      case 1: return 'bg-amber-400'; // Restrict - yellow/amber
      case 2: return 'bg-green-500'; // On - green
      default: return 'bg-zinc-400';
    }
  };
  
  const getTrackColor = () => {
    if (disabled && !readOnly) return 'bg-zinc-100';
    switch (position) {
      case 0: return 'bg-zinc-200';
      case 1: return 'bg-amber-100';
      case 2: return 'bg-green-100';
      default: return 'bg-zinc-200';
    }
  };
  
  // Check if interaction is blocked (either disabled or readOnly)
  const isInteractionBlocked = disabled || readOnly;
  
  const handleClick = (newPosition) => {
    if (isInteractionBlocked) return;
    const positionValues = ['off', 'restrict', 'on'];
    onChange?.(positionValues[newPosition]);
  };
  
  // Handle drag interaction
  const handleTrackClick = (e) => {
    if (isInteractionBlocked) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    
    // Determine which third was clicked
    let newPosition;
    if (percentage < 0.33) {
      newPosition = 0;
    } else if (percentage < 0.66) {
      newPosition = 1;
    } else {
      newPosition = 2;
    }
    
    handleClick(newPosition);
  };
  
  return (
    <div 
      className={cn('flex flex-col', sizeConfig.spacing, className)}
      data-testid={dataTestId}
    >
      {/* Slider Track */}
      <div 
        className={cn(
          'relative rounded-full transition-colors duration-200 border border-zinc-300',
          sizeConfig.track,
          getTrackColor(),
          isInteractionBlocked ? 'cursor-default' : 'cursor-pointer',
          disabled && !readOnly && 'opacity-60'
        )}
        onClick={handleTrackClick}
        role="slider"
        aria-valuenow={position}
        aria-valuemin={0}
        aria-valuemax={2}
        aria-disabled={isInteractionBlocked}
        aria-readonly={readOnly}
        tabIndex={isInteractionBlocked ? -1 : 0}
        onKeyDown={(e) => {
          if (isInteractionBlocked) return;
          if (e.key === 'ArrowLeft' && position > 0) {
            handleClick(position - 1);
          } else if (e.key === 'ArrowRight' && position < 2) {
            handleClick(position + 1);
          }
        }}
      >
        {/* Position markers/dots */}
        <div className="absolute inset-0 flex items-center justify-between px-2 pointer-events-none">
          <div className={cn(
            'w-1.5 h-1.5 rounded-full transition-colors',
            position === 0 ? 'bg-white' : 'bg-zinc-400/30'
          )} />
          <div className={cn(
            'w-1.5 h-1.5 rounded-full transition-colors',
            position === 1 ? 'bg-white' : 'bg-zinc-400/30'
          )} />
          <div className={cn(
            'w-1.5 h-1.5 rounded-full transition-colors',
            position === 2 ? 'bg-white' : 'bg-zinc-400/30'
          )} />
        </div>
        
        {/* Sliding Thumb */}
        <div 
          className={cn(
            'absolute top-0.5 rounded-full shadow-md transition-all duration-200 ease-out',
            sizeConfig.thumb,
            getThumbColor()
          )}
          style={{
            left: `calc(${thumbOffset}% - ${position * 2}px)`,
            transform: position === 2 ? 'translateX(-100%)' : position === 1 ? 'translateX(-50%)' : 'translateX(0)',
            marginLeft: position === 0 ? '2px' : position === 2 ? '-2px' : '0'
          }}
        />
      </div>
      
      {/* Labels below slider */}
      {showLabels && (
        <div className="flex justify-between px-0.5">
          <button
            type="button"
            onClick={() => handleClick(0)}
            disabled={disabled}
            className={cn(
              'transition-colors font-medium',
              sizeConfig.label,
              position === 0 ? 'text-zinc-700' : 'text-zinc-400 hover:text-zinc-600',
              disabled && 'cursor-not-allowed'
            )}
          >
            {labels.off}
          </button>
          <button
            type="button"
            onClick={() => handleClick(1)}
            disabled={disabled}
            className={cn(
              'transition-colors font-medium',
              sizeConfig.label,
              position === 1 ? 'text-amber-600' : 'text-zinc-400 hover:text-zinc-600',
              disabled && 'cursor-not-allowed'
            )}
          >
            {labels.restrict}
          </button>
          <button
            type="button"
            onClick={() => handleClick(2)}
            disabled={disabled}
            className={cn(
              'transition-colors font-medium',
              sizeConfig.label,
              position === 2 ? 'text-green-600' : 'text-zinc-400 hover:text-zinc-600',
              disabled && 'cursor-not-allowed'
            )}
          >
            {labels.on}
          </button>
        </div>
      )}
    </div>
  );
};

export default ThreePositionSlider;
